<template>
    <div class="autoenrolments__edit_form">
        <form-text
            :label="'Name'"
            :name="`autoenrolments-edit-form-name`"
            :required="true"
            :v-validate="{ required: true }"
            :data-vv-as="'name'"
            v-model="autoenrolmentsName"
        ></form-text>
        <form-textarea
            :label="'Description'"
            :name="`autoenrolments-edit-form-description`"
            v-model="autoenrolmentsDescription"
        ></form-textarea>
        <form-radio-horizontal
            :label="'Type'"
            :name="`autoenrolments-edit-form-type`"
            :disabled="isEditMode"
            :v-validate="'required'"
            :data-vv-as="'type'"
            :items="[
                {
                    name: 'Organisation unit',
                    value: 'organisationUnit'
                },
                {
                    name: 'Group',
                    value: 'group'
                }
            ]"
            :value="autoenrolmentsType"
            @input="autoenrolmentsSetType"
        />
        <template v-if="autoenrolmentsType === 'organisationUnit'">
            <form-select-org-units
                :label="'Target audience'"
                :name="'autoenrolments-edit-form-orgunit'"
                :items="listUnits"
                :value="autoenrolmentsTargetAudience"
                :workspace-id="loggedInUserWorkspaceId"
                :v-validate="'required'"
                :required="true"
                :keep-alive="true"
                @input="autoenrolmentsSetTargetAudience"
            ></form-select-org-units>
        </template>
        <template v-if="autoenrolmentsType === 'group'">
            <form-select
                :label="'Target audience'"
                :advanced-search-active="false"
                :items="listGroups"
                :get-items-dispatch="'lmsSuperadminGroupsGet'"
                :get-items-dispatch-data="loggedInUserWorkspaceId"
                :required="true"
                :name="'autoenrolments-edit-form-groups'"
                :value="autoenrolmentsTargetAudience"
                :multiple="true"
                :v-validate="'required'"
                :data-vv-as="'target'"
                @input="autoenrolmentsSetTargetAudience"
            ></form-select>
        </template>
        <form-select
            :data-vv-as="'courses'"
            :get-pagination-getter="'getEbsCoursesPagination'"
            :get-items-getter="'getEbsCoursesMappedIdNameStatusLabels'"
            :get-items-getter-mapped="'getEbsCoursesMappedLabelValue'"
            :get-items-dispatch="'ebsCoursesGetAdmin'"
            :search-queryable="true"
            :table-labels="[
                {
                    key: 'name',
                    title: 'Name',
                    type: 'string',
                    sortable: true,
                    queryable: true
                }
            ]"
            :item-name="['course', 'courses']"
            :label="'Courses'"
            :multiple="true"
            :name="`autoenrolments-edit-form-courses`"
            :required="true"
            :value="autoenrolmentsSuggestedCourses"
            :v-validate="{ required: true }"
            @input="autoenrolmentsSetSuggestedCourses"
            @input-table="autoenrolmentsSetSuggestedCourses"
        ></form-select>
        <form-checkbox
            :name="`autoenrolments-edit-form-reenrol`"
            :label="'On save, run on the selected target audience'"
            v-model="autoenrolmentsSetReenrolment"
        >This will not enrol users who are already enrolled or have completed the course(s)</form-checkbox>
        <form-checkbox
            :name="`autoenrolments-edit-form-status`"
            :label="''"
            :switcher="true"
            v-model="autoenrolmentsStatus"
        >
            To start enrolling the target audience on invitation set the auto-enrolment to active. <br>
            To stop enrolling the target audience on invitation set the auto-enrolment to inactive.
        </form-checkbox>
        <pop-notify
            ref="popnotifyvalidation"
            :class="'pop__notify--validation'"
            :title-text="`Something's not quite right`"
            :content-text="'Please check that you have filled out all the fields in the form correctly and try again.'"
            :cancel-button-text="'Ok'"
            :submit-button-active="false"
        />
    </div>
</template>

<script>
import PopNotify from '@/components/default/shared/PopNotify'
import { validateBeforeRouteLeave, watchContentChangeEmit } from '@/helpers/mixins'
import { User } from '@/User'
import FormSelect from '@/components/default/forms/FormSelect'
import FormRadioHorizontal from '@/components/default/forms/FormRadioHorizontal'
import FormCheckbox from '@/components/default/forms/FormCheckbox'
import FormSelectOrgUnits from '@/components/default/forms/FormSelectOrgUnits'
import FormText from '@/components/default/forms/FormText'
import FormTextarea from '@/components/default/forms/FormTextarea'

export default {
  components: {
    FormTextarea,
    FormText,
    FormSelectOrgUnits,
    FormCheckbox,
    FormRadioHorizontal,
    FormSelect,
    PopNotify
  },
  name: 'autoenrolments-edit-form',
  data () {
    return {
      user: new User()
    }
  },
  mixins: [validateBeforeRouteLeave, watchContentChangeEmit],
  computed: {
    loggedInUserWorkspaceId () {
      return this.$store.getters.getAuthUserSecondStageTokenDataWorkspaceId
    },
    isAddMode () {
      return this.$route.name.includes('autoenrolments-add')
    },
    isEditMode () {
      return this.$route.name.includes('autoenrolments-edit')
    },
    routeQueryView () {
      return this.$route.query.view
    },
    autoenrolmentRouteId () {
      return this.$route.params.id
    },
    autoenrolmentsName: {
      get () {
        return this.$store.getters.getLmsUserAutoenrolmentName
      },
      set (value) {
        this.$store.dispatch('lmsUserAutoenrolmentSetName', value)
      }
    },
    autoenrolmentsDescription: {
      get () {
        return this.$store.getters.getLmsUserAutoenrolmentDescription
      },
      set (value) {
        this.$store.dispatch('lmsUserAutoenrolmentSetDescription', value)
      }
    },
    autoenrolmentsStatus: {
      get () {
        return this.$store.getters.getLmsUserAutoenrolmentStatus === 1 || this.$store.getters.getLmsUserAutoenrolmentStatus === '1'
      },
      set (value) {
        this.$store.dispatch('lmsUserAutoenrolmentSetStatus', value === true ? 1 : 0)
      }
    },
    autoenrolmentsType () {
      return this.$store.getters.getLmsUserAutoenrolmentType
    },
    autoenrolmentsTargetAudience () {
      return this.$store.getters.getLmsUserAutoenrolmentTargetAudience
    },
    autoenrolmentsSuggestedCourses () {
      return this.$store.getters.getLmsUserAutoenrolmentSuggestedCourses
    },
    autoenrolmentsSetReenrolment: {
      get () {
        return this.$store.getters.getLmsUserAutoenrolmentReenrolment
      },
      set (value) {
        this.$store.dispatch('lmsUserAutoenrolmentSetReenrolment', value)
      }
    },
    watchContent () {
      return this.autoenrolmentsTargetAudience + this.autoenrolmentsSuggestedCourses
    },
    listUnits () {
      const orgUnits = this.$store.getters.getLmsSuperadminOrgUnitsChildren
      return orgUnits
    },
    listGroups () {
      const groups = this.$store.getters.getLmsSuperadminGroupsMappedLabelValue
      return groups
    },
    listAllowedUnits () {
      const orgUnits = this.$store.getters.getLmsSuperadminOrgUnitsMappedIdName
      const notAllowedUnits = this.listUnitsWithCourses
      const allowedUnits = orgUnits.map(obj => obj.id).filter(obj => !notAllowedUnits.includes(obj))

      return allowedUnits
    },
    listUnitsWithCourses () {
      const items = this.$store.getters.getLmsUserAutoenrolments
      const filteredItems = items && items.length ? items.filter(obj => obj.targetType === 'organisationUnit') : items
      let outputItemsArr = []

      for (let item of filteredItems) {
        if (item.targets && item.targets.length) {
          for (let target of item.targets) {
            outputItemsArr.push(target.id)
          }
        }
      }

      return outputItemsArr
    },
    listGroupsWithCourses () {
      const items = this.$store.getters.getLmsUserAutoenrolments
      const filteredItems = items && items.length ? items.filter(obj => obj.targetType === 'group') : items
      let outputItemsArr = []

      for (let item of filteredItems) {
        if (item.targets && item.targets.length) {
          for (let target of item.targets) {
            outputItemsArr.push(target.id)
          }
        }
      }

      return outputItemsArr
    }
  },
  methods: {
    autoenrolmentsSetType (value) {
      this.$store.dispatch('lmsUserAutoenrolmentSetType', value)
      this.autoenrolmentsSetTargetAudience()
    },
    autoenrolmentsSetSuggestedCourses (value) {
      this.$store.dispatch('lmsUserAutoenrolmentSetSuggestedCourses', value)
    },
    autoenrolmentsSetTargetAudience (value) {
      this.$store.dispatch('lmsUserAutoenrolmentSetTargetAudience', value)
    }
  },
  mounted () {
    if (this.isAddMode && this.routeQueryView) {
      this.autoenrolmentsSetType(this.routeQueryView)
    }
  },
  inject: ['$validator']
}
</script>

<style lang="scss">
    @import "~@/assets/scss/components/ebs/autoenrolments/autoenrolmentsaddedit";
</style>
